/**
 * Where each installed app starts.
 *
 * `APP_HOME_PATHS` is the path of each app's first `APP_NAV` member — its start
 * tab (nav spec §4.10). The paths also appear in each manifest's `start_url`
 * and `scope`, and `pwaManifests.test.ts` pins this table to both so the three
 * can never drift. `APP_INSTALL_REDIRECTS` derives from it rather than
 * repeating it.
 *
 * Manifest scope is the OS's boundary (document-level navigations only). The
 * app's own reach — what it may restore or hold in a side pane — is scope plus
 * its members' subtrees: see `features/workspace/appScope.ts`.
 */
import { ATTENTION_ROUTES } from '@shared/constants/routes';
import { destinationById } from '@shared/navigation/destinations';

import { startTabFor } from './appNav';
import { APP_SLUGS } from './installedApp';

import type { AppSlug } from './installedApp';

/** The path of a start destination; a typo in APP_NAV must fail at load, not launch an app into `/`. */
function startPath(slug: AppSlug): string {
  const id = startTabFor(slug);
  const dest = destinationById(id);
  if (!dest) throw new Error(`APP_NAV.${slug} starts on '${id}', which is no catalog destination`);
  return dest.path;
}

/** Slug → the app's own landing path, derived from its start destination. */
export const APP_HOME_PATHS: Record<AppSlug, string> = Object.fromEntries(
  APP_SLUGS.map(slug => [slug, startPath(slug)])
) as Record<AppSlug, string>;

/**
 * Install URLs for the per-area PWAs.
 *
 * Each area's HTML shell is served at `/app/<area>/` so it can advertise that
 * area's manifest; these redirects send the one-time install visit on to the
 * real surface. The manifests' `start_url` points at the target directly, so a
 * normal launch never passes through here.
 *
 * Derived from APP_HOME_PATHS so an area's landing path is written down once.
 * Lives here rather than in AppRouter.tsx so that module exports only
 * components (a React Fast Refresh boundary) and the route table is testable
 * without importing the whole authenticated app.
 */
export const APP_INSTALL_REDIRECTS = (
  Object.entries(APP_HOME_PATHS) as [AppSlug, string][]
).map(([slug, to]) => ({ path: `/app/${slug}`, to }));

/**
 * The running app's home, or the inbox in a browser tab.
 *
 * `null` is the root Conway app, which owns the whole origin and has no home of
 * its own — it keeps the inbox it has always landed on.
 */
export function homePathFor(slug: AppSlug | null): string {
  return slug === null ? ATTENTION_ROUTES.root : APP_HOME_PATHS[slug];
}
